
import searchRouter from '@/plugins/searchRouter.client';
import { initState } from '@/store/search/filter';
import { APPLY_FILTER } from '@/utils/amplitudeEvent/amplitudeEvents.js';
import {
  AGE_OPTIONS,
  CLASS_TYPE,
  DAY_OF_WEEK,
  FILTER_PERIOD_OPTIONS,
  FILTER_TIME_OPTIONS,
  PRICE_OPTIONS,
  WEEK_TYPE,
} from '@/utils/filter/filterOptions';
import { queryEndTimeIdx, queryStartTimeIdx } from '@/utils/filter/filterUtils';
import { datePattern } from '@/utils/regex';
import { getISODay } from 'date-fns';
import { mapActions, mapGetters, mapMutations } from 'vuex';

import FilterTextField from '@/components/search/filter/FilterTextField';
import FilterButton from './FilterButton.vue';
import SelectedFilterOption from './SelectedFilterOption.vue';

export default {
  name: 'SearchHeader',
  components: {
    FilterTextField,
    FilterButton,
    SelectedFilterOption,
  },
  mixins: [searchRouter],
  computed: {
    ...mapGetters(['getIsHiddenNavHeader']),
    ...mapGetters('search/filter', ['keyword']),
    ...mapGetters('filter', ['categoryList', 'categoryDifficultyMap']),
    subCategoryList() {
      const { selectedCategory } = this.$route.query;
      if (!selectedCategory) return [];
      return (
        this.categoryList.find(({ id }) => id === parseInt(selectedCategory))
          ?.children || []
      );
    },
    selectedCategoryDifficultyList() {
      return this.categoryDifficultyMap[this.$route.query.selectedCategory];
    },
    /**
     * @description
     * 선택된 필터들을 화면 상에 나열한다.
     */
    selectedFilterArray() {
      const {
        startTimeIdx,
        endTimeIdx,
        selectedPrice,
        selectedClassType,
        selectedCategory,
        selectedSubCategory,
        difficulty,
        onlyDiscounted,
      } = this.$route.query;
      let filterArray = []; // {query: startTimeIdx, queryValue: "7", text: "오후 7시"}
      // startTimeIdx: "7" 시간, endTimeIdx: "14" 시간
      if (startTimeIdx || endTimeIdx)
        filterArray = [this.timeFilterButtonObject()];

      // selectedPrice: '0' | '1' | '2' | '3'
      if (selectedPrice)
        filterArray = [...filterArray, this.priceFilterButtonObject()];
      // selectedClassType: '0' | '1' | '2' | '3' 중복 선택 가능
      if (selectedClassType)
        filterArray = [...filterArray, ...this.classTypeFilterButtonObject()];

      // category: '0' | '1' | '2' | ...
      if (selectedCategory) {
        const numCategoryId = parseInt(selectedCategory);
        if (!isNaN(numCategoryId)) {
          filterArray = [
            ...filterArray,
            this.categoryFilterButtonObject(numCategoryId),
          ];

          if (selectedSubCategory) {
            const numSubCategoryId = parseInt(selectedSubCategory);
            if (!isNaN(numSubCategoryId)) {
              filterArray = [
                ...filterArray,
                this.subCategoryFilterButtonObject(numSubCategoryId),
              ];
            }
          }
          // difficulty: 'A1' | 'A2' | 'B1' | 'B2'...
          if (difficulty) {
            const difficultyFilterButtonObject =
              this.difficultyFilterButtonObject(difficulty);
            if (difficultyFilterButtonObject)
              filterArray = [...filterArray, difficultyFilterButtonObject];
          }
        }
      }

      // onlyDiscounted: 'true'
      if (onlyDiscounted)
        filterArray = [...filterArray, this.onlyDiscountedFilterButtonObject()];
      this.SET_HAS_ViEW_FIlTER(filterArray.length > 0);
      return filterArray;
    },
    /**
     * @description
     * selectedPeriodOption: 'TODAY' | 'ALL_DATES' | 'SEVEN_DAYS' | 'SELECT_CALENDAR' | 'THIRTY_DAYS'
     * selectedWeekType: 'ALL_DATES' | 'WEEK_DAY' | 'WEEK_END'
     * selectedDayOfWeek: 1,2,3,4,5,6,7
     */
    selectedSchedule() {
      const { selectedPeriodOption, selectedWeekType, selectedDayOfWeek } =
        this.$route.query;
      let displayDateArray = [];

      // selectedPeriodOption: 'TODAY' | 'SEVEN_DAYS' | 'SELECT_CALENDAR' | 'THIRTY_DAYS'
      if (
        FILTER_PERIOD_OPTIONS.find(
          ({ value }) => value === selectedPeriodOption,
        )
      )
        displayDateArray = [this.selectedScheduleFilterText()];

      // selectedWeekType: 'ALL_DATES' | 'WEEK_DAY' | 'WEEK_END'
      if (WEEK_TYPE.find(({ value }) => value === selectedWeekType)) {
        displayDateArray = [
          ...displayDateArray,
          WEEK_TYPE.find(({ value }) => value === selectedWeekType)?.text,
        ];

        return displayDateArray.join(', ');
      }

      // selectedDayOfWeek: '1' || '1,2,3,4,5,6,7'
      if (selectedDayOfWeek) {
        const dayStrArray = selectedDayOfWeek
          .split(',')
          .filter(day => {
            const numberDay = parseInt(day);
            return !isNaN(numberDay) && numberDay >= 0 && numberDay < 8;
          })
          .map(day => Number(day))
          .sort();

        const dayOfWeekTextArray = dayStrArray.map(
          day => DAY_OF_WEEK.find(({ value }) => value === day)?.text,
        );

        displayDateArray = [...displayDateArray, dayOfWeekTextArray.join(', ')];
      }
      return displayDateArray.length
        ? displayDateArray.join(', ')
        : '모든 일정';
    },
    /**
     * @description
     * route.query.selectedAge가 없으면 '모든 연령'을 반환
     * @returns
     * '모든 연령' | 5세 ~ 13세 | 5세, 8세 | 5세 외 2개
     */
    selectedAge() {
      if (this.$route.query?.selectedAge) {
        const ageArray = this.$route.query?.selectedAge.split(',') || [];
        // 오름차순 정렬
        const applyFilterAgeList = ageArray
          .filter(age => {
            const numberAge = Number(age);
            return (
              !isNaN(numberAge) &&
              numberAge >= AGE_OPTIONS[0].value &&
              numberAge <= AGE_OPTIONS[AGE_OPTIONS.length - 1].value
            );
          })
          .sort((a, b) => Number(a) - Number(b));
        if (!applyFilterAgeList.length) return '모든 연령';
        if (applyFilterAgeList.length > 2)
          return `${
            AGE_OPTIONS.find(
              ({ value }) => value === Number(applyFilterAgeList[0]),
            )?.text
          } 외 ${applyFilterAgeList.length - 1}개`;

        return applyFilterAgeList
          .map(
            age => AGE_OPTIONS.find(({ value }) => value === Number(age))?.text,
          )
          .join(', ');
      }

      return '모든 연령';
    },
    isSearchQuery() {
      if (this.$route.query.order !== undefined) return true;
      if (this.$route.query.listType !== undefined) return true;
      if (this.$route.query.keyword !== undefined) return true;
      return false;
    },
    hasViewFilter() {
      return this.selectedFilterArray.length > 0;
    },
  },
  created() {
    if (this.$route.query?.keyword) this.SET_KEYWORD(this.$route.query.keyword);
    // 선택된 카테고리가 있을 때, 카테고리 난이도 조회 및 설정
    const { selectedCategory } = this.$route.query;

    if (
      selectedCategory &&
      this.categoryDifficultyMap[selectedCategory] === undefined
    ) {
      this.getCategoryDifficultyList(selectedCategory);
    }
  },
  beforeMount() {
    if (this.$route.query.f === true) this.focusKeyword = true;
  },
  destroyed() {
    this.SET_KEYWORD('');
  },
  methods: {
    ...mapMutations('search/filter', [
      'SET_KEYWORD',
      'SET_SELECTED_AGE',
      'SET_CLASS_TYPE',
      'SET_PRICE',
      'SET_CATEGORY',
      'SET_SUB_CATEGORY',
      'SET_DIFFICULTY',
      'SET_START_TIME_IDX',
      'SET_END_TIME_IDX',
      'SET_ONLY_DISCOUNTED',
    ]),
    ...mapMutations('search', [
      'SET_HAS_ViEW_FIlTER',
      'SET_ACTIVE_BOTTOM_DRAWER_FILTER',
    ]),
    ...mapActions('filter', ['getCategoryDifficultyList']),
    resetKeyword() {
      this.SET_KEYWORD('');
    },
    searchKeyword() {
      window.scroll({ top: 0 });
      this.setAllFiltersAndSearch();
    },
    //
    setAllFiltersAndSearch() {
      // 'set_schedule'로 검색페이지 인입되고, '적용'버튼 누를 때 route.query에 order는 없다.
      if (
        (this.$route.query.order &&
          this.buttonTypeFromProduct === 'set_schedule') ||
        this.buttonTypeFromProduct === 'same_schedule'
      )
        this.setButtonTypeFromProduct();
      this.logUserCustomEvent(APPLY_FILTER);
      // 목적
      // 1. 동작 유도: 수업 페이지 -> 검색 페이지 -> 필터 적용(히스토리 안 남게 함) -> 검색 페이지 -> 수업 페이지
      // 2. 선생님 id, name을 url query로 유지하기 위함
      if (this.buttonTypeFromProduct === 'set_schedule') {
        this.$router.replace({
          name: 'search',
          query: {
            ...this.$route.query,
            id: this.$route.query?.id,
            name: this.$route.query?.name,
            order:
              this.buttonTypeFromProduct === 'set_schedule'
                ? 'RELEASE_DATE'
                : this.$route.query?.order,
          },
        });
        return;
      }
      this.onKeywordSearch(this.keyword);
    },
    onChangeKeywordTextInput(v) {
      // this.changeKeyword(v);
      this.SET_KEYWORD(v);
    },
    timeFilterButtonObject() {
      const startTimeIdx = queryStartTimeIdx(this.$route.query?.startTimeIdx);
      const endTimeIdx = queryEndTimeIdx(this.$route.query?.endTimeIdx);
      return {
        query: `startTimeIdx`,
        removeState: () => {
          this.SET_START_TIME_IDX(initState.startTimeIdx);
          this.SET_END_TIME_IDX(initState.endTimeIdx);
        },
        pushQuery: {
          ...this.$route.query,
          startTimeIdx: undefined,
          endTimeIdx: undefined,
        },
        // 00:00 ~ 23:00
        text: `${FILTER_TIME_OPTIONS[startTimeIdx].value
          .toString()
          .padStart(2, '0')}:00부터 ${FILTER_TIME_OPTIONS[endTimeIdx].value
          .toString()
          .padStart(2, '0')}:00까지`,
      };
    },
    priceFilterButtonObject() {
      return {
        query: `selectedPrice`,
        removeState: () => this.SET_PRICE(initState.selectedPrice),
        pushQuery: {
          ...this.$route.query,
          selectedPrice: undefined,
        },
        text: PRICE_OPTIONS.find(
          ({ value }) => value === this.$route.query.selectedPrice,
        )?.text,
      };
    },
    classTypeFilterButtonObject() {
      const classTypeArray = this.$route.query.selectedClassType
        ?.split(',')
        .filter(classType => {
          const numberClassType = Number(classType);
          return (
            !isNaN(numberClassType) &&
            numberClassType >= 0 &&
            numberClassType < CLASS_TYPE.length
          );
        })
        .map(classType => Number(classType))
        .sort();
      // return ['원데이', '다회차', '구독' , '1:1']
      return classTypeArray.reduce(
        (acc, cur) => [
          ...acc,
          {
            query: `selectedClassType_${cur}`,
            queryValue: cur,
            removeState: () =>
              this.SET_CLASS_TYPE(
                classTypeArray?.length === 1
                  ? initState.selectedClassType
                  : classTypeArray.filter(classType => classType !== cur),
              ),
            pushQuery: {
              ...this.$route.query,
              selectedClassType:
                classTypeArray?.length === 1
                  ? undefined
                  : classTypeArray
                      .filter(classType => classType !== cur)
                      .join(','),
            },
            text:
              cur === 3
                ? '1:1 수업만 보기'
                : CLASS_TYPE.find(({ value }) => value === cur)?.text,
          },
        ],
        [],
      );
    },
    categoryFilterButtonObject(numCategoryId) {
      return {
        query: `selectedCategory`,
        removeState: () => {
          this.SET_CATEGORY(initState.selectedCategory);
          this.SET_SUB_CATEGORY(initState.selectedSubCategory);
          this.SET_DIFFICULTY(initState.difficulty);
        },
        pushQuery: {
          ...this.$route.query,
          selectedCategory: undefined,
          selectedSubCategory: undefined,
          difficulty: undefined,
        },
        text: this.categoryList.find(({ id }) => id === numCategoryId)?.name,
      };
    },
    subCategoryFilterButtonObject(numSubCategoryId) {
      return {
        query: `selectedSubCategory`,
        removeState: () => {
          this.SET_SUB_CATEGORY(initState.selectedSubCategory);
        },
        pushQuery: {
          ...this.$route.query,
          selectedSubCategory: undefined,
        },
        text: this.subCategoryList.find(({ id }) => id === numSubCategoryId)
          ?.name,
      };
    },
    // difficulty: 'A1' | 'A2' | ...
    difficultyFilterButtonObject(difficultyTerm) {
      if (!this.selectedCategoryDifficultyList) return;
      const targetDiff = this.selectedCategoryDifficultyList.find(
        ({ term }) => term === difficultyTerm,
      );

      if (!targetDiff) return;
      return {
        query: `difficulty`,
        removeState: () => this.SET_DIFFICULTY(initState.difficulty),
        pushQuery: {
          ...this.$route.query,
          difficulty: undefined,
        },
        text: `${targetDiff.term}: ${targetDiff.termKo}`,
      };
    },
    onlyDiscountedFilterButtonObject() {
      return {
        query: `onlyDiscounted`,
        removeState: () => this.SET_ONLY_DISCOUNTED(initState.onlyDiscounted),
        pushQuery: {
          ...this.$route.query,

          onlyDiscounted: undefined,
        },
        text: '할인 수업 보기',
      };
    },
    // 'SELECT_CALENDAR'면 classStartDate=2023-10-25&classEndDate=2023-10-28
    selectedScheduleFilterText() {
      const { classStartDate, classEndDate } = this.$route.query;
      if (
        this.$route.query.selectedPeriodOption === 'SELECT_CALENDAR' &&
        datePattern.test(classStartDate) &&
        datePattern.test(classEndDate)
      ) {
        return this.formatSelectedDateText();
      } else {
        return FILTER_PERIOD_OPTIONS.find(
          ({ value }) => value === this.$route.query.selectedPeriodOption,
        )?.text;
      }
    },
    // 일정 필터 표시 > '직접 선택' 버튼 클릭 시 > 포멧 만들어주는 메서드
    formatSelectedDateText() {
      const { classStartDate, classEndDate } = this.$route.query;
      const [, sm, sd] = classStartDate.split('-');
      const [, em, ed] = classEndDate.split('-');
      const sDay = DAY_OF_WEEK.find(
        ({ value }) =>
          value === getISODay(new Date(classStartDate.replace(/-/g, '/'))),
      )?.text;
      const eDay = DAY_OF_WEEK.find(
        ({ value }) =>
          value === getISODay(new Date(classEndDate.replace(/-/g, '/'))),
      )?.text;
      return `${sm}.${sd} ${sDay} - ${em}.${ed} ${eDay}`;
    },
  },
};
